import { selectVisibleLeaseComps } from "@/features/survey/leaseCompsSelectors";
import {
  selectPOICategories,
  selectPOIs,
  selectPOIsLoading,
} from "@/features/survey/poisSelectors";
import { selectVisiblePolygons } from "@/features/survey/polygonSelectors";
import { useGetSurveyMapKeysBySurveyQuery } from "@/services/survey-mapKeys";
import { useGetPolygonsQuery } from "@/services/survey-polygons";
import { useAppSelector } from "@app/hooks";
import { mapboxToken } from "@constants/index";
import {
  BAM_MAP_STYLE_SOURCES,
  BAM_MAP_STYLES,
  defaultZoomLevel,
} from "@constants/mapStyles";
import MapKey from "@features/survey/components/mapControls/MapKey";
import { useGetEmeraldBingTilesQuery } from "@services/bingApi";
import { useFetchMarinePortsQuery } from "@services/mapApi";
import { useUploadMarketMapFileMutation } from "@services/media";
import { isEmptyObjectFromStr } from "@shared/utils";
import * as mapUtil from "@shared/utils/map";
import { CustomMarker } from "@survey-types/survey.mapInterface.types";
import { DEFAULT_POLYGON_STYLE } from "@survey-types/survey.polygon.types";
import mapboxgl, { StyleSpecification } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useEffect, useRef, useState } from "react";

interface MarketMapRenderedViewProps {
  index: number;
  isClientShortList?: boolean;
}

const MarketMapRenderedView = (props: MarketMapRenderedViewProps) => {
  const { index, isClientShortList } = props;
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const survey = useAppSelector((state) => state.survey.currentSurvey);
  const selectMediaFiles = useAppSelector((state) => state.survey.mediaFiles);
  const [uploadMarketMapFile] = useUploadMarketMapFileMutation();
  const { data: marinePortsRes } = useFetchMarinePortsQuery();
  const { data: bingTilesData } = useGetEmeraldBingTilesQuery();
  const { isFetching: isMapKeysFetching, isSuccess: isMapKeysLoaded } =
    useGetSurveyMapKeysBySurveyQuery(survey?.id || "", {
      refetchOnMountOrArgChange: false,
    });
  const isIncludeLeaseComps = useAppSelector(
    (state) => state.survey?.currentSurvey?.isIncludeLeaseComps,
  );
  const isIncludePOI = useAppSelector(
    (state) => state.survey?.currentSurvey?.isIncludePOI,
  );
  const isIncludePolygons = useAppSelector(
    (state) => state.survey?.currentSurvey?.isIncludePolygons,
  );
  const { isLoading: isPolygonsLoading } = useGetPolygonsQuery(survey?.id || "", {
    skip: !survey?.id,
    refetchOnMountOrArgChange: false,
  });
  const polygons = useAppSelector(selectVisiblePolygons);
  const shouldRenderPolygons = Boolean(isIncludePolygons);
  const getComps = useAppSelector(selectVisibleLeaseComps);
  const leaseComps = isIncludeLeaseComps ? getComps : [];
  // POI categories are fetched at Builder.tsx level to avoid duplicate requests
  // Use Redux state for rendering (automatically updated by the top-level query via onQueryStarted)
  const poiCategories = useAppSelector(selectPOICategories);
  const poiIsLoading = useAppSelector(selectPOIsLoading);
  // Use Redux state for POI data (updated by optimistic updates)
  const poiPoints = useAppSelector(selectPOIs);
  // Refs always hold the latest POI data so async .then() callbacks never
  // read stale closure snapshots (mirrors MarketMapPreview's latestPOIs pattern).
  const latestPOIPoints = useRef(poiPoints);
  const latestPOICategories = useRef(poiCategories);
  const latestIsIncludePOI = useRef(isIncludePOI);
  const latestPolygons = useRef(polygons);
  const polygonMouseEnterHandlerRef = useRef<(() => void) | null>(null);
  const polygonMouseLeaveHandlerRef = useRef<(() => void) | null>(null);
  const polygonMouseLeaveCursorRef = useRef("");
  const [hasUploadedMapForRender, setHasUploadedMapForRender] = useState(false);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [arePolygonLayersReady, setArePolygonLayersReady] = useState(false);
  const [areMarkersReady, setAreMarkersReady] = useState(false);

  const handleUploadMapData = async () => {
    try {
      if (!survey || index !== 0) return;
      const formData = await mapUtil.marketMapGeneration(survey);
      uploadMarketMapFile(formData as FormData).catch((_err: Error) => {
        console.error("Failed to upload market map file");
      });
    } catch (_error) {
      console.error("Failed to generate market map data");
    }
  };

  const updateMapView = (newCenter: [number, number], newZoom: number) => {
    const map = mapRef.current;
    if (!map) return;

    map.setCenter(newCenter);
    map.setZoom(newZoom);
  };

  // Keep POI refs current on every render so callbacks always see latest data.
  latestPOIPoints.current = poiPoints;
  latestPOICategories.current = poiCategories;
  latestIsIncludePOI.current = isIncludePOI;
  latestPolygons.current = polygons;

  const fitBoundsForMapReset = () => {
    if (!survey) return;
    const markers = (
      mapRef.current as unknown as Record<string, CustomMarker[] | undefined>
    )?._markers;

    const bounds = new mapboxgl.LngLatBounds();
    const uniqueCoords = new Map<string, mapboxgl.LngLat>();
    let polygonCoordinateCount = 0;

    markers?.forEach((marker: CustomMarker) => {
      const coord = marker.getLngLat();
      const coordKey = marker.getElement().id;

      // Check if this is a POI marker
      if (coordKey.startsWith("poi-")) {
        // Only include POIs if isIncludePOI is true
        if (!isIncludePOI) {
          return;
        }

        const poiId = coordKey.replace("poi-", "");
        const poi = poiPoints?.find((p) => p.extId === poiId);

        // Skip if POI doesn't exist or is hidden
        if (!poi || poi.isHidden) {
          return;
        }

        // Include POI in bounds
        uniqueCoords.set(coordKey, coord);
        return;
      }

      // Find the property for this marker
      const property = survey.properties?.find((prop) =>
        coordKey.includes(prop.propertyId),
      );

      // Skip if property doesn't exist (deleted) or is hidden
      if (!property || property.isHidden) {
        if (property?.isHidden) {
          return;
        } else {
          const leaseProperty = leaseComps?.find((comp) =>
            coordKey.includes(comp.leaseCompId),
          );
          if (!leaseProperty || leaseProperty.isHidden) {
            return;
          }
        }
      }

      uniqueCoords.set(coordKey, coord);
    });

    uniqueCoords.forEach((coord) => {
      bounds.extend(coord);
    });
    
    if (shouldRenderPolygons) {
      latestPolygons.current.forEach((polygon) => {
        polygon.coordinates.forEach((ring) => {
          ring.forEach((coordinate) => {
            bounds.extend([coordinate[0], coordinate[1]]);
            polygonCoordinateCount++;
          });
        });
      });
    }

    if (
      (uniqueCoords.size > 0 || polygonCoordinateCount > 0) &&
      mapRef.current
    ) {
      // Adjust padding to account for map key dimensions (matches MarketMapPreview)
      const { width: mapKeyWidth, height: mapKeyHeight } = survey?.isIncludeMapKey
        ? mapUtil.getMapKeyRect()
        : { width: 0, height: 0 };
      const mapKeyPadding =
        mapKeyHeight > mapKeyWidth
          ? { bottom: 50, left: 50 + mapKeyWidth }
          : { bottom: 50 + mapKeyHeight, left: 50 };

      mapRef.current.fitBounds(bounds, {
        padding: { top: 50, right: 50, ...mapKeyPadding },
        maxZoom: 18,
        duration: 0,
      });
    }
  };

  const fetchGeoJson = async () => {
    try {
      if (mapRef.current && survey) {
        mapUtil.addMarinePortLayer(
          mapRef.current,
          marinePortsRes?.features || [],
          survey.isMarinePorts ?? false,
        );
        mapUtil.raisePolygonLayers(mapRef.current);
      }
    } catch (_error) {
      console.error("Failed to fetch marine ports data");
    }
  };

  const updateMapPoints = () => {
    if (!survey || !mapRef.current) return;
    const filteredSurvey = isClientShortList
      ? {
          ...survey,
          properties:
            survey.properties?.filter(
              (property) => property.isClientShortlist,
            ) || [],
        }
      : survey;
    mapUtil.deleteMarkers(mapRef.current);
    mapUtil
      .getMapPointsWithImages(
        filteredSurvey,
        selectMediaFiles ?? undefined,
        leaseComps,
      )
      .then(async (points: any) => {
        if (mapRef?.current && points) {
          // Read from refs to get latest POI data, not stale closure snapshot
          const currentPOIPoints = latestPOIPoints.current;
          const currentPOICategories = latestPOICategories.current;
          const currentIsIncludePOI = latestIsIncludePOI.current;
          if (currentIsIncludePOI && currentPOIPoints && currentPOICategories) {
            const poiFeatures = mapUtil.createPOIFeatures(
              currentPOIPoints,
              currentPOICategories,
            );
            points.features.push(...poiFeatures);
          }
          await mapUtil.getMarkerConfig(points as never, mapRef.current, true, false);
          setAreMarkersReady(true);
        }
      });
  };

  useEffect(() => {
    if (!survey || !mapboxToken) {
      return;
    }

    mapboxgl.accessToken = mapboxToken;
    const marketMapBamData =
      survey.marketMapBamData &&
      typeof JSON.parse(survey.marketMapBamData) === "object" &&
      !isEmptyObjectFromStr(survey.marketMapBamData)
        ? JSON.parse(survey.marketMapBamData)
        : {
            mapCentroid: mapUtil.getCenterPoint(survey.properties || []),
            mapZoomLevel: defaultZoomLevel,
          };

    const style =
      survey.marketMapStyle === BAM_MAP_STYLES.emeraldBingHybrid
        ? (bingTilesData as unknown as StyleSpecification | undefined)
        : (BAM_MAP_STYLE_SOURCES[
            survey.marketMapStyle || Object.keys(BAM_MAP_STYLE_SOURCES)[0]
          ] as unknown as StyleSpecification | undefined);
    if (mapContainerRef.current) {
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: style || BAM_MAP_STYLE_SOURCES.cbreLight,
        center: marketMapBamData.mapCentroid || [-95, 40],
        zoom: marketMapBamData.mapZoomLevel || defaultZoomLevel,
        preserveDrawingBuffer: true,
      });

      // Filter survey properties if isClientShortList is true
      const filteredSurvey = isClientShortList
        ? {
            ...survey,
            properties:
              survey.properties?.filter(
                (property) => property.isClientShortlist,
              ) || [],
          }
        : survey;

      mapUtil
        .getMapPointsWithImages(
          filteredSurvey,
          selectMediaFiles ?? undefined,
          leaseComps,
        )
        .then(async (points: any) => {
          if (mapRef?.current && points) {
            // Read from refs to get latest POI data at callback execution time
            const currentPOIPoints = latestPOIPoints.current;
            const currentPOICategories = latestPOICategories.current;
            const currentIsIncludePOI = latestIsIncludePOI.current;
            if (currentIsIncludePOI && currentPOIPoints && currentPOICategories) {
              const poiFeatures = mapUtil.createPOIFeatures(
                currentPOIPoints,
                currentPOICategories,
              );
              points.features.push(...poiFeatures);
            }
            await mapUtil.getMarkerConfig(
              points as never,
              mapRef.current,
              false,
              false,
            );
            setAreMarkersReady(true);
          }
        });
      mapRef.current?.on("load", async () => {
        mapRef.current?.resize();
        const surveyBamData = JSON.parse(survey.marketMapBamData || "{}");
        if (shouldRenderPolygons && latestPolygons.current.length > 0) {
          fitBoundsForMapReset();
        } else if (!surveyBamData?.mapReset) {
          updateMapView(
            marketMapBamData.mapCentroid || [-95, 40],
            marketMapBamData.mapZoomLevel || defaultZoomLevel,
          );
        } else {
          fitBoundsForMapReset();
        }
        setIsMapLoaded(true);
      });
    }
    return () => {
      if (mapRef?.current) {
        mapRef?.current.remove();
      }
      setIsMapLoaded(false);
      setHasUploadedMapForRender(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

   useEffect(() => {
    updateMapPoints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaseComps]);

  // Sync POI markers: re-render whenever POI data or visibility changes
  // Matches MarketMapPreview's exact pattern for consistency
  useEffect(() => {
    if (!mapRef.current) return;

    if (!isIncludePOI) {
      // If POI is disabled, still re-render to remove POI markers
      if (poiCategories || poiPoints) {
        updateMapPoints();
      }
      return;
    }
    if (!poiCategories || !poiPoints) return;
    // Re-render map points when POI data changes or visibility toggles
    updateMapPoints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poiCategories, poiPoints, isIncludePOI]);

  // Render polygons on the map (for PDF export)
  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    const syncPolygons = () => {
      mapUtil.syncPolygonLayers({
        map,
        polygons,
        shouldRenderPolygons,
        currentDrawingPolygon: [],
        defaultPolygonStyle: DEFAULT_POLYGON_STYLE,
        polygonMouseEnterHandlerRef,
        polygonMouseLeaveHandlerRef,
        polygonMouseLeaveCursorRef,
      });
      setArePolygonLayersReady(true);
    };

    // Initial sync attempt + future syncs when style finishes loading/reloading.
    syncPolygons();
    map.on("styledata", syncPolygons);
    map.on("load", syncPolygons);

    return () => {
      map.off("styledata", syncPolygons);
      map.off("load", syncPolygons);
    };
  }, [polygons, isMapLoaded, shouldRenderPolygons]);


  useEffect(() => {
    if (!isMapLoaded || !areMarkersReady || isPolygonsLoading || !survey) return;

    const mapData = JSON.parse(survey.marketMapBamData || "{}");
    if (shouldRenderPolygons && polygons.length > 0) {
      fitBoundsForMapReset();
    } else if (mapData.mapReset || !mapData.mapCentroid || !mapData.mapZoomLevel) {
      fitBoundsForMapReset();
    } else {
      updateMapView(
        mapData.mapCentroid || mapUtil.getCenterPoint(survey.properties || []),
        mapData.mapZoomLevel || defaultZoomLevel,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMapLoaded,
    areMarkersReady,
    isPolygonsLoading,
    polygons,
    shouldRenderPolygons,
    survey?.marketMapBamData,
  ]);

  useEffect(() => {
    const map = mapRef.current;
    const container = mapContainerRef.current;
    if (!map || !container || !isMapLoaded || !areMarkersReady || isPolygonsLoading) return;

    const resizeObserver = new ResizeObserver(() => {
      if (!container.clientWidth || !container.clientHeight) return;
      map.resize();
      if (shouldRenderPolygons && polygons.length > 0) {
        fitBoundsForMapReset();
      }
    });
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapLoaded, areMarkersReady, isPolygonsLoading, polygons, shouldRenderPolygons]);

  // Sync property markers: re-render when property visibility / media changes
  useEffect(() => {
    if (!survey || !mapRef.current) return;
    updateMapPoints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey?.properties, selectMediaFiles]);

  useEffect(() => {
    if (!marinePortsRes || !survey) return;
    fetchGeoJson();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marinePortsRes]);

  useEffect(() => {
    if (
      !bingTilesData ||
      !mapRef.current ||
      survey?.marketMapStyle !== BAM_MAP_STYLES.emeraldBingHybrid
    )
      return;
    mapRef.current.setStyle(bingTilesData as unknown as StyleSpecification);
    mapRef.current.once("idle", () => {
      marinePortsRes?.features &&
        marinePortsRes.features.length > 1 &&
        mapRef?.current &&
        mapUtil.addMarinePortLayer(
          mapRef.current,
          marinePortsRes?.features,
          survey?.isMarinePorts ?? false,
        );
      if (mapRef.current) mapUtil.raisePolygonLayers(mapRef.current);
    });
  }, [bingTilesData, survey?.marketMapStyle]);

  useEffect(() => {
    if (
      index !== 0 ||
      !survey?.id ||
      !isMapLoaded ||
      hasUploadedMapForRender
    ) {
      return;
    }

    if (survey?.isIncludeMapKey && (!isMapKeysLoaded || isMapKeysFetching)) {
      return;
    }

    if (isIncludePOI && poiIsLoading) {
      return;
    }

    if (isPolygonsLoading || !arePolygonLayersReady || !areMarkersReady) {
      return;
    }

    // Give the map a moment to render markers and the map key with POI styles before capture.
    const timeoutId = window.setTimeout(() => {
      handleUploadMapData()
        .then(() => {
          setHasUploadedMapForRender(true);
        })
        .catch((_error) => {
          console.error("Failed to upload market map data");
        });
    }, 1200);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    index,
    survey?.id,
    isMapLoaded,
    hasUploadedMapForRender,
    survey?.isIncludeMapKey,
    isMapKeysLoaded,
    isMapKeysFetching,
    isIncludePOI,
    poiIsLoading,
    poiCategories,
    isPolygonsLoading,
    arePolygonLayersReady,
    areMarkersReady,
    polygons,
    shouldRenderPolygons,
  ]);

  if (!survey) {
    return null;
  }

  return (
    <div
      id={`marketmap-${index}`}
      ref={mapContainerRef}
      className="h-full w-full"
    >
      {survey?.isIncludeMapKey && <MapKey />}
    </div>
  );
};

export default MarketMapRenderedView;
